import { useRef } from "react";
import { ImagePlus, Play, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useSlotUpload } from "@/components/useSlotUpload";

const MediaSlot = ({
  src,
  slot,
  caption,
  video = false,
  videoSrc = null,
  ratio = "aspect-[4/5]",
  dark = false,
  className = "",
  cursorLabel = "VIEW",
  onClick,
}) => {
  const inputRef = useRef(null);
  const { uploaded, save, clear } = useSlotUpload(slot);
  const effectiveSrc = src || uploaded;
  const base = `group relative overflow-hidden ${ratio} ${className}`;

  const pickFile = () => inputRef.current?.click();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File terlalu besar untuk preview cepat", {
        description: "Maks 4MB. Untuk foto permanen, taruh file di folder public dan set src di data.js.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const ok = save(reader.result);
      if (ok === false) {
        toast.error("Penyimpanan browser penuh", { description: "Hapus beberapa upload atau set permanen via data.js." });
      } else {
        toast.success("Foto terpasang!", { description: `${slot} — tersimpan di browser ini. Buat permanen lewat data.js.` });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onRemove = (e) => {
    e.stopPropagation();
    clear();
    toast("Upload dihapus", { description: `${slot} kembali ke placeholder.` });
  };

  if (!effectiveSrc) {
    return (
      <div
        data-testid={`media-slot-${(slot || "placeholder").toLowerCase()}`}
        onClick={pickFile}
        data-cursor="UPLOAD"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && pickFile()}
        aria-label={`Upload ${video ? "video" : "photo"} untuk ${slot}`}
        className={`${base} media-empty flex cursor-pointer flex-col items-center justify-center gap-3 border border-dashed p-6 text-center transition-colors duration-300 ${
          dark
            ? "border-white/25 text-paper/50 hover:border-ember hover:text-paper"
            : "border-ink/25 text-ink/45 hover:border-burgundy hover:text-ink"
        }`}
      >
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`file-input-${(slot || "placeholder").toLowerCase()}`} />
        {video ? <Play className="h-6 w-6" /> : <Upload className="h-6 w-6" />}
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.3em]">
          {video ? "Add your video" : "Klik untuk upload foto"}
        </span>
        {slot && <span className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-60">{slot}</span>}
        {caption && <span className="text-xs italic opacity-70">{caption}</span>}
      </div>
    );
  }

  return (
    <figure onClick={onClick} data-cursor={video || videoSrc ? "PLAY" : cursorLabel} className={base}>
      {video && !videoSrc ? (
        <video src={effectiveSrc} className="h-full w-full object-cover" controls={false} muted playsInline />
      ) : (
        <img
          src={effectiveSrc}
          alt={caption || slot || "Kin Hakan media"}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      )}
      {videoSrc && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ember/90 text-ink transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-0.5 h-6 w-6" fill="currentColor" />
          </span>
        </span>
      )}
      {caption && (
        <figcaption className="absolute bottom-0 left-0 bg-ink/70 px-3 py-1.5 font-display text-[10px] uppercase tracking-[0.2em] text-paper">
          {caption}
        </figcaption>
      )}
      {!src && uploaded && (
        <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <button
            data-testid={`replace-${(slot || "").toLowerCase()}`}
            onClick={(e) => { e.stopPropagation(); pickFile(); }}
            aria-label="Ganti foto"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-ink/80 text-paper hover:bg-ember hover:text-ink"
          >
            <ImagePlus className="h-4 w-4" />
          </button>
          <button
            data-testid={`remove-${(slot || "").toLowerCase()}`}
            onClick={onRemove}
            aria-label="Hapus foto"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-ink/80 text-paper hover:bg-burgundy"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        </div>
      )}
    </figure>
  );
};

export default MediaSlot;
